import React from 'react'
import {withGoogleReCaptcha} from 'react-google-recaptcha-v3'
import {Row, Col, Spin, Result, Typography} from 'antd'
import ReactComponent from '../app/ReactComponent'
import CardVerification from './card-verification/CardVerification'
import OtpVerification from './otp-verification/OtpVerification'
import RegistrationForm from './registration-form/RegistrationForm'
import dayjs from 'dayjs'
import {STATUS, COUNTRIES, DASHBOARD_TEXTS, FORM_TEXTS} from './helpers/misc.js'
import App from '../app/App.js'
import {validateForm, getPersonalInformationObject, normalizePhone} from './dashboard/dashboardUtils'

const {Title} = Typography

class CustomerPortal extends ReactComponent {
  state = {
    form_submitted_successfully: false,
    scrollToField: null,
    // Card verification state
    card_code: '',
    verification_status: STATUS.UNAUTHORIZED,
    loading: false,
    error: null,

    // OTP state
    otp: '',
    otp_sent: false,
    otp_verified: false,
    resend_otp_available: true,
    resend_otp_countdown: 0,

    // Form state
    form_data: {
      gender: '',
      first_name: '',
      last_name: '',
      street: '',
      city: '',
      zip: '',
      country: '',
      email: '',
      phone: '',
      birth_date: '',
      terms_accepted: false
    },
    customer_data_raw: null, // Store the original customer data
    form_errors: {},
    status: STATUS.UNAUTHORIZED
  }

  async componentDidMount() {

  }

  handleVerifyCard = async () => {
    void this.updateState({loading: true, error: null})
    sessionStorage.removeItem('otp_verify')

    try {
      const {googleReCaptchaProps} = this.props
      const recaptcha_token = await googleReCaptchaProps.executeRecaptcha('benu_card_verification')
      const result = await this.$app.verifyCard(this.state.card_code, recaptcha_token)
      await this.updateState({status: result.status, verification_status: result.status})

      if(result.status === STATUS.AUTHORIZED && sessionStorage.getItem('otp_verify') === 'true') {
        await this.handleCustomerData()
      } else if((result.status === STATUS.OTP_READY || result.status === STATUS.ASSIGNED_WITH_EMAIL) && !this.state.otp_sent) {
        await this.sendOtp()
      }
    } catch (error) {
      void this.updateState({verification_status: STATUS.ERROR})
      App.$e(DASHBOARD_TEXTS.cardVerificationError)
    } finally {
      void this.updateState({loading: false})
    }
  }

  sendOtp = async () => {
    try {
      // Call the new method to send OTP
      await this.$app.sendVerificationCode()

      // Start resend countdown (60 seconds)
      let countdown = 60
      const timer = setInterval(() => {
        countdown--
        this.updateState({
          resend_otp_countdown: countdown,
          resend_otp_available: countdown <= 0
        })

        if(countdown <= 0) {
          clearInterval(timer)
        }
      }, 1000)

      void this.updateState({
        otp_sent: true,
        resend_otp_countdown: 60,
        resend_otp_available: false
      })

      App.$toast(DASHBOARD_TEXTS.otpSentSuccess)

    } catch (error) {
      App.$e(DASHBOARD_TEXTS.otpSentError)
    }
  }

  handleResendOtp = () => {
    if(this.state.resend_otp_available) {
      void this.sendOtp()
    }
  }

  handleVerifyOtp = async () => {
    void this.updateState({loading: true})
    try {
      const response = await this.$app.verifyCode(this.state.otp)

      if(response.status === 'verified') {

        sessionStorage.setItem('otp_verify', 'true')
        await this.updateState({otp_verified: true, status: STATUS.AUTHORIZED, is_session_verified: true})
        await this.handleCustomerData()
        void this.updateState({loading: false})
      } else {
        void this.updateState({loading: false})
        App.$e(response.message || DASHBOARD_TEXTS.otpVerificationError)
      }
    } catch (error) {
      void this.updateState({loading: false})
      App.$e(DASHBOARD_TEXTS.otpVerificationError)
    }
  }

  handleCustomerData = async () => {
    try {

      const {personal_information} = await this.$app.getCustomer()

      // Remove consents for update api call
      if(personal_information.hasOwnProperty('consents'))
        delete personal_information.consents

      void this.updateState({
        customer_data_raw: personal_information, // Store the whole object
        form_data: {
          /*MA: Nezda se nam ze by tohle slo udelat jinak/lepe? Odpovim rovnou ja - ano zda */
          gender: personal_information.gender === 1 ? 1 : personal_information.gender === 2 ? 2 : '',
          first_name: personal_information.first_name,
          last_name: personal_information.last_name,
          street: personal_information.address.address1,
          city: personal_information.address.city,
          zip: personal_information.address.zip,
          country: Object.values(COUNTRIES).find(c => c.code === personal_information.address.country_code)?.code || '', /*MA: Again, tenhle kod zacinam mit rad, jak je na kazdym druhym radku, to je fakt pecka. Teda tenhle tu jeste neni, tady je zameneny name za code :D :D, takze tohle jeste navic nefunguje. Davam muj oblibeny LOL */
          email: personal_information.email,
          phone: normalizePhone(personal_information.phone),
          terms_accepted: personal_information.agreement.agreement_gtc === 1, /*MA: WHAT THE HELL? */
          birth_date: personal_information.birthdate ? dayjs(personal_information.birthdate).format('DD.MM.YYYY') : ''
        }
      })
    } catch (error) {
      App.$e(DASHBOARD_TEXTS.formGenericError)
    }

  }

  capitalizeWords(str) {
    return str
      .split(' ')
      .map(word => {
        if(word.length === 0) return ''
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      })
      .join(' ')
  }

  handleFormChange = (field, value) => {

    if(field === 'first_name' || field === 'last_name' || field === 'city')
      value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()

    if(field === 'email')
      value = value.toLowerCase()

    if(field === 'street') {
      value = this.capitalizeWords(value)
    }

    if(field === 'zip')
      value = value.replace(/\s+/g, '')

    void this.updateState({
      form_data: {
        ...this.state.form_data,
        [field]: value
      },
      form_errors: {
        ...this.state.form_errors,
        [field]: null
      }
    })
  }

  handleCardCodeChange = (cardCode) => {
    void this.updateState({card_code: cardCode})
  }

  handleOtpChange = (otp) => {
    void this.updateState({otp})
  }

  handleSubmitForm = async () => {
    const {form_data, card_code, customer_data_raw, verification_status} = this.state
    const {googleReCaptchaProps} = this.props

    const errors = validateForm(form_data)
    if(Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0]
      await this.updateState({
        form_errors: errors,
        scrollToField: firstErrorField
      })
      return
    }

    void this.updateState({loading: true})

    try {
      const recaptcha_token = await googleReCaptchaProps.executeRecaptcha('benu_submit_form')

      if(verification_status !== STATUS.NEW) {
        const updatedData = getPersonalInformationObject(form_data, customer_data_raw)

        // Pass recaptcha_token to updateCustomer
        const response = await this.$app.updateCustomer(updatedData, recaptcha_token)

        if(response.status === 'updated') {
          void this.updateState({
            form_submitted_successfully: true
          })
        } else {
          App.$e(response.message || DASHBOARD_TEXTS.formGenericError)
        }
      } else {
        const registrationData = getPersonalInformationObject(form_data)
        const response = await this.$app.registerCustomer(card_code, registrationData, recaptcha_token)

        if(response.status === 'created') {

          void this.updateState({
            form_submitted_successfully: true,
            verification_status: STATUS.CREATED
          })

        } else {
          App.$e(response.message || DASHBOARD_TEXTS.formGenericError)
        }
      }
    } catch (error) {
      const invalidParams = error.response?.data?.error?.error_data?.invalid_params
      const emailExists = Array.isArray(invalidParams) && invalidParams.some(
        param => param.name === 'email' && param.reason === 'already_exists'
      )

      if(emailExists) {
        void this.updateState({
          form_errors: {
            ...this.state.form_errors,
            email: DASHBOARD_TEXTS.formEmailConflictError // Using formEmailConflictError as it's more appropriate
          },
          scrollToField: 'email'
        })
      } else {
        App.$e(DASHBOARD_TEXTS.formGenericError)
      }
    } finally {
      void this.updateState({loading: false})
    }
  }

  _renderCardVerification() {
    return (
      <CardVerification
        cardCode={this.state.card_code}
        error={this.state.error}
        loading={this.state.loading}
        onCardCodeChange={this.handleCardCodeChange}
        onVerifyClick={this.handleVerifyCard}
      />
    )
  }

  _renderOtpVerification() {
    return (
      <OtpVerification
        cardNumber={this.state.card_code}
        otp={this.state.otp}
        resendOtpAvailable={this.state.resend_otp_available}
        resendOtpCountdown={this.state.resend_otp_countdown}
        onOtpChange={this.handleOtpChange}
        onVerifyOtp={this.handleVerifyOtp}
        onResendOtp={this.handleResendOtp}
      />
    )
  }

  _renderRegistrationForm() {
    return (
      <div className="verification-box">
        <div style={{marginBottom: '24px', textAlign: 'center'}}>
          <Title level={3} style={{color: '#142654', marginTop: 0}}>
            {this.state.verificationStatus === 'new' ? FORM_TEXTS.newCardTitle : FORM_TEXTS.updateCardTitle}
          </Title>
          <p>Pre správnu registráciu vyplňte všetky údaje vrátane diakritiky.</p>
          {this.state.verificationStatus === 'new' && <p>Ak už máte plastovú alebo virtuálnu Vernostnú kartu BENU, svoje údaje si môžete jednoducho aktualizovať na stránke <a href="https://vernost.benulekaren.sk" target="_blank">vernost.benulekaren.sk</a>.</p>}
        </div>
        <RegistrationForm
          formData={this.state.form_data}
          formErrors={this.state.form_errors}
          verificationStatus={this.state.verification_status}
          onFormChange={this.handleFormChange}
          onSubmit={this.handleSubmitForm}
          loading={this.state.loading}
          scrollToField={this.state.scrollToField}
          onScrollComplete={() => this.updateState({scrollToField: null})}
        />

      </div>
    )
  }

  _renderSuccessMessage() {
    const {verification_status} = this.state
    const isRegistration = verification_status === STATUS.CREATED

    const title = isRegistration
      ? DASHBOARD_TEXTS.formRegisterSuccessTitle
      : DASHBOARD_TEXTS.formUpdateSuccessTitle

    return (
      <div className="verification-box">
        <Result
          status="success"
          title={title}
        />
      </div>
    )
  }

  render() {
    const {status, verification_status} = this.state

    let content = null

    const showCardVerification = status === STATUS.UNAUTHORIZED &&
      [STATUS.UNAUTHORIZED, STATUS.ERROR, STATUS.NOT_FOUND].includes(verification_status)

    const showOtpVerification = !this.state.otp_verified && (status === STATUS.OTP_READY || verification_status === STATUS.ASSIGNED_WITH_EMAIL)

    const showRegistrationForm = status === STATUS.AUTHORIZED ||
      [STATUS.NEW, STATUS.ASSIGNED_NO_EMAIL].includes(verification_status)

    if(this.state.form_submitted_successfully) {
      content = this._renderSuccessMessage()
    } else if(showCardVerification) {
      content = this._renderCardVerification()
    } else if(showOtpVerification) {
      content = this._renderOtpVerification()
    } else if(showRegistrationForm) {
      content = this._renderRegistrationForm()
    }

    return (
      <div className="card-verification">
        <Spin spinning={this.state.loading} size="large">
          <Row justify="center">
            <Col xs={22} sm={18} md={12} lg={8}>
              {content}
            </Col>
          </Row>
        </Spin>
      </div>
    )
  }
}

export default withGoogleReCaptcha(CustomerPortal)